import {
  Stack, Text, Group, ThemeIcon, Button, Box, Code, Alert,
  PinInput, Loader, Modal, CopyButton, ActionIcon, Tooltip, Badge,
  PasswordInput,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import {
  IconShieldCheck, IconShieldOff, IconQrcode,
  IconCopy, IconCheck, IconAlertTriangle, IconTrash, IconLock,
} from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { QRCodeSVG } from 'qrcode.react'
import { useState } from 'react'
import { mfaApi, type TotpBeginResponse } from '@/api/mfa'
import { formatApiError } from '@/api/errors'
import { Callout, WidgetCard } from '@/components/ui'

export function TotpCard() {
  const qc = useQueryClient()
  const [setupData, setSetupData] = useState<TotpBeginResponse | null>(null)
  const [code, setCode] = useState('')
  const [removeOpen, setRemoveOpen] = useState(false)
  const [pwModalOpen, setPwModalOpen] = useState(false)
  const [pwInput, setPwInput] = useState('')
  const [pwError, setPwError] = useState('')

  const statusQuery = useQuery({
    queryKey: ['mfa', 'status'],
    queryFn: mfaApi.status,
  })

  const beginMutation = useMutation({
    mutationFn: (password?: string) => mfaApi.totpBegin(password),
    onSuccess: (data) => {
      setSetupData(data)
      setCode('')
      setPwModalOpen(false)
      setPwInput('')
      setPwError('')
    },
    onError: (err: any) => {
      const detail = err?.response?.data?.detail
      if (detail === 'invalid_password') {
        setPwError('Senha incorreta. Tente novamente.')
      } else if (detail === 'password_quality_failed') {
        setPwError('Sua senha atual não atende a política do Kanidm. Troque-a antes de configurar TOTP.')
      } else {
        notifications.show({
          color: 'red', title: 'Erro ao iniciar setup',
          message: formatApiError(err, 'Falha contatando Kanidm'),
        })
      }
    },
  })

  const handleStartSetup = () => {
    // Sempre abre modal de confirmacao de senha — exigido pela politica
    // Kanidm (MfaRequired) para users sem primary credential, e tambem
    // confirma identidade antes de mexer com 2FA.
    setPwInput('')
    setPwError('')
    setPwModalOpen(true)
  }

  const verifyMutation = useMutation({
    mutationFn: ({ token, value }: { token: string; value: number }) => {
      if (!token || !Number.isInteger(value) || value < 0 || value > 999999) {
        return Promise.reject(new Error('Código inválido — digite os 6 dígitos'))
      }
      return mfaApi.totpVerify(token, value)
    },
    onSuccess: () => {
      notifications.show({
        color: 'teal', title: 'TOTP configurado',
        message: 'Seu autenticador esta vinculado. Use código de 6 dígitos no próximo login.',
      })
      setSetupData(null)
      setCode('')
      qc.invalidateQueries({ queryKey: ['mfa', 'status'] })
    },
    onError: (err) => {
      notifications.show({
        color: 'red', title: 'Código inválido',
        message: formatApiError(err, 'Tente novamente — código expira em 30s.'),
      })
      setCode('')
    },
  })

  const cancelMutation = useMutation({
    mutationFn: (token: string) => mfaApi.totpCancel(token),
    onSettled: () => {
      setSetupData(null)
      setCode('')
    },
  })

  const removeMutation = useMutation({
    mutationFn: () => mfaApi.totpRemove('totp'),
    onSuccess: () => {
      notifications.show({
        color: 'orange', title: 'TOTP removido',
        message: 'Seu user não tem mais MFA configurado.',
      })
      setRemoveOpen(false)
      qc.invalidateQueries({ queryKey: ['mfa', 'status'] })
    },
    onError: (err) => {
      notifications.show({
        color: 'red', title: 'Erro ao remover',
        message: formatApiError(err),
      })
    },
  })

  const triggerVerify = (raw: string) => {
    if (!setupData || raw.length !== 6) return
    const value = parseInt(raw, 10)
    if (Number.isNaN(value)) return
    verifyMutation.mutate({ token: setupData.session_token, value })
  }

  if (statusQuery.isLoading) {
    return (
      <WidgetCard p="md">
        <Group justify="center" py="xl"><Loader /></Group>
      </WidgetCard>
    )
  }

  const hasTotp = statusQuery.data?.has_totp ?? false

  return (
    <WidgetCard p="md">
      <Stack gap="md">
        <Group gap="sm" wrap="nowrap">
          <ThemeIcon
            size="xl" radius="md" variant="light"
            color={hasTotp ? 'teal' : 'gray'}
          >
            {hasTotp ? <IconShieldCheck /> : <IconShieldOff />}
          </ThemeIcon>
          <Box style={{ flex: 1 }}>
            <Text fw={600}>Autenticação em duas etapas (TOTP)</Text>
            <Text size="sm" c="dimmed">
              {hasTotp
                ? 'Configurada — código gerado pelo seu app autenticador.'
                : 'Não configurada — login do broker ZTNA exige 2FA.'}
            </Text>
          </Box>
          <Badge ml="auto" color={hasTotp ? 'teal' : 'red'}>
            {hasTotp ? 'Ativa' : 'Inativa'}
          </Badge>
        </Group>

        <Callout title="Como funciona" variant="info">
          <Stack gap={4}>
            <Text size="sm">1. Clique em "Configurar TOTP" — gera segredo único no Kanidm.</Text>
            <Text size="sm">2. Scaneie o QR code com Google Authenticator (ou Aegis, 1Password, Bitwarden).</Text>
            <Text size="sm">3. Digite o código de 6 dígitos — verificação automática ao 6o dígito (Enter também confirma).</Text>
            <Text size="sm" c="orange">
              Após configurar, o login do broker (gitlab.lab.tectoylabs.com.br via ConsoleAI ZTNA) vai exigir o código TOTP.
            </Text>
          </Stack>
        </Callout>

        {!setupData && (
          <Group>
            <Button
              type="button"
              leftSection={<IconQrcode size={18} />}
              onClick={handleStartSetup}
              loading={beginMutation.isPending}
            >
              {hasTotp ? 'Reconfigurar TOTP' : 'Configurar TOTP'}
            </Button>
            {hasTotp && (
              <Button
                type="button"
                variant="subtle" color="red"
                leftSection={<IconTrash size={18} />}
                onClick={() => setRemoveOpen(true)}
              >
                Remover TOTP
              </Button>
            )}
          </Group>
        )}

        {setupData && (
          <Box
            style={{
              border: '1px solid var(--mantine-color-default-border)',
              borderRadius: 8,
              padding: 16,
            }}
          >
            <Stack gap="md">
              <Text fw={600}>Scaneie o QR code com seu app autenticador:</Text>
              <Box p="md" bg="white" style={{ borderRadius: 8, alignSelf: 'flex-start' }}>
                <QRCodeSVG value={setupData.otpauth_url} size={220} level="M" />
              </Box>

              <Stack gap={4}>
                <Text size="sm" c="dimmed">Conta: <Code>{setupData.accountname}</Code></Text>
                <Text size="sm" c="dimmed">
                  Algoritmo: <Code>{setupData.algo}</Code> · {setupData.digits} dígitos · {setupData.step}s
                </Text>
              </Stack>

              <Alert icon={<IconAlertTriangle size={18} />} color="yellow" variant="light">
                <Stack gap={4}>
                  <Text size="sm" fw={600}>Caso scan não funcione, digite o segredo manualmente:</Text>
                  <Group gap="xs">
                    <Code style={{ fontSize: 13, wordBreak: 'break-all' }}>{setupData.secret_b32}</Code>
                    <CopyButton value={setupData.secret_b32}>
                      {({ copied, copy }) => (
                        <Tooltip label={copied ? 'Copiado' : 'Copiar'}>
                          <ActionIcon variant="subtle" onClick={copy}>
                            {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </CopyButton>
                  </Group>
                </Stack>
              </Alert>

              {/* G2/G9: form com Enter dispara Confirmar; botoes type=button explicitos */}
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  triggerVerify(code)
                }}
              >
                <Stack gap={4}>
                  <Text size="sm" fw={600}>Digite o código de 6 dígitos:</Text>
                  <Group>
                    {/* G3: autoFocus no primeiro slot quando setupData aparece */}
                    <PinInput
                      length={6} type="number"
                      value={code}
                      onChange={setCode}
                      onComplete={triggerVerify}
                      autoFocus
                      disabled={verifyMutation.isPending}
                    />
                    <Button
                      type="submit"
                      disabled={code.length !== 6}
                      loading={verifyMutation.isPending}
                    >
                      Confirmar
                    </Button>
                    <Button
                      type="button"
                      variant="subtle" color="gray"
                      onClick={() => cancelMutation.mutate(setupData.session_token)}
                      disabled={verifyMutation.isPending}
                    >
                      Cancelar
                    </Button>
                  </Group>
                </Stack>
              </form>
            </Stack>
          </Box>
        )}

        <Modal
          opened={pwModalOpen}
          onClose={() => { setPwModalOpen(false); setPwInput(''); setPwError('') }}
          title="Confirme sua senha"
          centered
        >
          <form
            onSubmit={(e) => {
              e.preventDefault()
              if (pwInput.length === 0) { setPwError('Informe sua senha.'); return }
              setPwError('')
              beginMutation.mutate(pwInput)
            }}
          >
            <Stack>
              <Text size="sm">
                Para configurar 2FA precisamos confirmar sua senha — exigência da
                política de segurança. A senha NÃO é armazenada.
              </Text>
              <PasswordInput
                label="Sua senha atual"
                placeholder="Digite sua senha"
                value={pwInput}
                onChange={(e) => { setPwInput(e.currentTarget.value); setPwError('') }}
                leftSection={<IconLock size={16} />}
                error={pwError || undefined}
                autoFocus
                data-sensitive="true"
              />
              <Group justify="flex-end">
                <Button type="button" variant="default"
                  onClick={() => { setPwModalOpen(false); setPwInput(''); setPwError('') }}>
                  Cancelar
                </Button>
                <Button type="submit" loading={beginMutation.isPending}
                  disabled={!pwInput}>
                  Continuar
                </Button>
              </Group>
            </Stack>
          </form>
        </Modal>

        <Modal
          opened={removeOpen}
          onClose={() => setRemoveOpen(false)}
          title="Remover TOTP"
          centered
        >
          <Stack>
            <Text size="sm">
              Após remover, você não conseguira mais logar no broker ZTNA até reconfigurar.
              Tem certeza?
            </Text>
            <Group justify="flex-end">
              <Button type="button" variant="default" onClick={() => setRemoveOpen(false)}>Cancelar</Button>
              <Button
                type="button"
                color="red"
                loading={removeMutation.isPending}
                onClick={() => removeMutation.mutate()}
              >
                Remover
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Stack>
    </WidgetCard>
  )
}
